import { EditablePriceScale, PriceScale, decimalToNumber } from '../../api/asset/assetApiTypes';
import { spaceOf } from '../../utils/useCases';

/**
 * The room price rows every room editor starts from, and the one mapping from
 * stored rows into editor rows. Shared by the object pages (create and detail)
 * and the product price dialog in Services (#2923) — three surfaces editing
 * the same kind of sheet, so one definition.
 */

/**
 * The rows a room sheet needs before it can price anything: the event pricing
 * rule (venues) prices from a `weekday` and a `weekend` row, everything else
 * (apartments) from one nightly row.
 *
 * Prices start as NaN (an EMPTY field), not 0: the operator must type a price
 * — an explicit 0 for a free room included — before the form saves. Starting
 * at 0 would save an untouched form as silently free, the forgotten-price trap
 * the empty≠0 input model exists to close.
 */
export const makeInitialRoomPriceScales = (useCase: string): EditablePriceScale[] => {
    if (spaceOf(useCase)?.flow === 'event') {
        return [
            { price: NaN, days: 'weekday', durationInMinutes: 1440, isFullDay: true },
            { price: NaN, days: 'weekend', durationInMinutes: 1440, isFullDay: true },
        ];
    }
    return [
        { price: NaN, days: null, durationInMinutes: 1440, isFullDay: true },
    ];
};

/**
 * Stored rows → editor rows, carrying the WHOLE scope.
 *
 * The save is replace-all and the backend matches each sent row to a stored
 * one on its full scope — days, full-day flag, duration, window and customer
 * class — archiving whatever it cannot match (`toPriceScalePayload`). So a
 * field dropped here is not "left alone": `customerClassId` was, and a class
 * rate then went back as a second GENERAL row with the same scope as the real
 * one, which the backend refuses as a duplicate (ADR-0019) — the room page
 * could not save prices at all once a class rate existed, and never showed the
 * class rates it had.
 */
export const toEditablePriceScales = (rows: PriceScale[] | null | undefined): EditablePriceScale[] =>
    (rows ?? []).map((s) => ({
        id: s.id,
        price: decimalToNumber(s.price) ?? 0,
        days: s.days,
        durationInMinutes: s.durationInMinutes ?? 1440,
        isFullDay: s.isFullDay,
        // Carried, not shown: the save has to send the row's whole scope back
        // or the backend treats it as a different row.
        startTime: s.startTime,
        endTime: s.endTime,
        additionalDaysPrice: decimalToNumber(s.additionalDaysPrice),
        customerClassId: s.customerClassId ?? null,
    }));
